import { Context, Controller } from '@hotwired/stimulus';
import {
  EVENT_TURBO_BEFORE_FETCH_REQUEST,
  EVENT_TURBO_BEFORE_FETCH_RESPONSE,
  EVENT_TURBO_FETCH_REQUEST_ERROR,
  EVENT_TURBO_FORM_SUBMIT_END,
} from 'src/events';
import { createSpinner } from 'src/utils/dom-create';

export default class TurboStreamLinkController extends Controller {
  public static values = {
    spinner: String,
  };

  declare private spinnerValue: 'replace' | 'prepend';

  private spinnerElement!: SVGSVGElement;
  private restoreChildren!: Node[];

  constructor(context: Context) {
    super(context);

    this.onStartRequest = this.onStartRequest.bind(this);
    this.onEndRequest = this.onEndRequest.bind(this);
  }

  connect(): void {
    this.spinnerElement = createSpinner('Loading...', 'size-3');
    this.restoreChildren = [...this.element.childNodes].map((node) =>
      node.cloneNode(true),
    );

    if (this.element.tagName === 'A') {
      this.element.addEventListener('click', this.onStartRequest);
    } else {
      this.element.addEventListener(
        EVENT_TURBO_BEFORE_FETCH_REQUEST,
        this.onStartRequest,
      );
    }
  }

  disconnect(): void {
    this.spinnerElement.remove();

    if (this.element.tagName === 'A') {
      this.element.removeEventListener('click', this.onStartRequest);
    } else {
      this.element.removeEventListener(
        EVENT_TURBO_BEFORE_FETCH_REQUEST,
        this.onStartRequest,
      );
    }

    document.removeEventListener(
      EVENT_TURBO_FORM_SUBMIT_END,
      this.onEndRequest,
    );
    document.removeEventListener(
      EVENT_TURBO_BEFORE_FETCH_RESPONSE,
      this.onEndRequest,
    );
    document.removeEventListener(
      EVENT_TURBO_FETCH_REQUEST_ERROR,
      this.onEndRequest,
    );
  }

  public onStartRequest(_event: Event) {
    // Ensure this is registered after any cancellation from ongoing item kicks-in
    setTimeout(() => {
      document.addEventListener(
        EVENT_TURBO_FORM_SUBMIT_END,
        this.onEndRequest,
        {
          once: true,
        },
      );
      document.addEventListener(
        EVENT_TURBO_BEFORE_FETCH_RESPONSE,
        this.onEndRequest,
        { once: true },
      );
      document.addEventListener(
        EVENT_TURBO_FETCH_REQUEST_ERROR,
        this.onEndRequest,
        { once: true },
      );
    }, 0);

    switch (this.spinnerValue) {
      case 'replace': {
        this.element.innerHTML = '';
        this.element.append(this.spinnerElement);
        break;
      }

      case 'prepend': {
        this.element.prepend(this.spinnerElement);
        break;
      }
    }
  }

  public onEndRequest(_event: Event) {
    document.removeEventListener(
      EVENT_TURBO_FORM_SUBMIT_END,
      this.onEndRequest,
    );
    document.removeEventListener(
      EVENT_TURBO_BEFORE_FETCH_RESPONSE,
      this.onEndRequest,
    );
    document.removeEventListener(
      EVENT_TURBO_FETCH_REQUEST_ERROR,
      this.onEndRequest,
    );

    this.element.innerHTML = '';
    this.element.append(...this.restoreChildren);
  }
}
